import { RecommendedAction } from "@/utils/vulnerabilityMatching";
import { GlassCard, GlassCardContent } from "@/components/ui/glass-card";
import { AlertTriangle, AlertCircle, CheckCircle2, ChevronRight, Monitor } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Device } from "@/types/device";

interface RecommendedActionsProps {
  actions: RecommendedAction[];
}

function DeviceList({ devices }: { devices: Device[] }) {
  if (devices.length === 0) return null;
  return (
    <ScrollArea className="max-h-[200px]">
      <div className="space-y-1.5 pt-3">
        {devices.map((d) => (
          <div
            key={d.id}
            className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-muted/50"
          >
            <div className="flex items-center gap-2">
              <Monitor className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium">{d.hostname || d.name || "Unknown"}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {d.serial_number && <span>{d.serial_number}</span>}
              <Badge variant="outline" className="text-xs">
                {d.os_version || "Unknown"}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

export function RecommendedActions({ actions }: RecommendedActionsProps) {
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());

  if (actions.length === 0) return null;

  const toggle = (id: string) => {
    setExpandedActions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getIcon = (urgency: string) => {
    switch (urgency) {
      case "critical":
        return <AlertTriangle className="h-5 w-5 text-destructive shrink-0" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 text-[hsl(45_90%_45%)] shrink-0" />;
      default:
        return <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />;
    }
  };

  const getBorderClass = (urgency: string) => {
    switch (urgency) {
      case "critical":
        return "border-destructive/30 bg-destructive/5";
      case "warning":
        return "border-[hsl(45_90%_45%)]/30 bg-[hsl(45_90%_45%)]/5";
      default:
        return "border-emerald-500/30 bg-emerald-500/5";
    }
  };

  const getLabel = (urgency: string) => {
    switch (urgency) {
      case "critical":
        return { text: "URGENT", className: "bg-destructive text-destructive-foreground" };
      case "warning":
        return { text: "RECOMMENDED", className: "bg-[hsl(45_90%_45%)] text-black" };
      default:
        return { text: "ALL CLEAR", className: "bg-emerald-500 text-white" };
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">Recommended Actions</h3>
        <Badge variant="secondary" className="text-xs">
          {actions.filter((a) => a.urgency !== "ok").length} action{actions.filter((a) => a.urgency !== "ok").length !== 1 ? "s" : ""} needed
        </Badge>
      </div>
      <div className="space-y-2">
        {actions.map((action) => {
          const isExpanded = expandedActions.has(action.id);
          const label = getLabel(action.urgency);
          return (
            <Collapsible key={action.id} open={isExpanded} onOpenChange={() => toggle(action.id)}>
              <div className={`rounded-xl border p-4 transition-colors ${getBorderClass(action.urgency)}`}>
                <CollapsibleTrigger className="w-full text-left" disabled={action.affectedDevices.length === 0}>
                  <div className="flex items-start gap-3">
                    {getIcon(action.urgency)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-[10px] ${label.className}`}>{label.text}</Badge>
                        <Badge variant="outline" className="text-[10px]">{action.platform}</Badge>
                      </div>
                      <p className="font-medium text-sm mt-1.5">{action.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{action.description}</p>
                    </div>
                    {action.affectedDevices.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                        <span>{action.affectedDevices.length} device{action.affectedDevices.length !== 1 ? "s" : ""}</span>
                        <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                      </div>
                    )}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <DeviceList devices={action.affectedDevices} />
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
}
